import {
    Connection,
    Keypair,
    LAMPORTS_PER_SOL,
    PublicKey,
    SystemProgram,
    TransactionInstruction,
    TransactionMessage,
    VersionedTransaction,
} from '@solana/web3.js';
import bs58 from 'bs58';
import { Jito } from '../utils/jito';

export class JitoSpamAndConfirmBundleTimeoutError extends Error {
    constructor() {
        super(JitoSpamAndConfirmBundleTimeoutError.name);
    }
}

export const useJito = () => {
    const jitoTsBackends = [new Jito(process.env.REACT_APP_JITO_BLOCK_ENGINE_URL!)];

    async function getTipInstructions(fromKeypair: Keypair, tipLamports: number) {
        if (tipLamports < 1000) throw new Error('Tip amount < 1000 lamports');
        if (tipLamports > LAMPORTS_PER_SOL) throw new Error('Tip amount > 1 SOL');
        const tipAccount = new PublicKey('ADaUMid9yfUytqMBgopwjb2DTLSokTSzL1zt6iGPaS49'); //(await jitoTsBackends[0].tipAccounts())[0]);

        return SystemProgram.transfer({
            fromPubkey: fromKeypair.publicKey,
            toPubkey: tipAccount,
            lamports: tipLamports + Math.floor(Math.random() * 1000),
        });
    }

    async function sendBundle(
        connection: Connection,
        txs: VersionedTransaction[],
        fromKeypair: Keypair,
        tipLamports: number,
        tipInstructionsIncluded: boolean = false,
        logMessage?: (message: string, type?: 'success' | 'error') => void,
    ) {
        const backendIndex = 0;
        if (!tipInstructionsIncluded) {
            // if (txs.length === 0) throw new Error('No transactions to send');
            if (txs.length > 4) throw new Error('Jito bundle can only contain 5 transactions at most.');
            const instructions = await getTipInstructions(fromKeypair, tipLamports);

            const { blockhash } = await connection.getLatestBlockhash('finalized');
            const tipMessage = new TransactionMessage({
                payerKey: fromKeypair.publicKey,
                recentBlockhash: blockhash,
                instructions: [instructions],
            }).compileToV0Message();
            const tipVersionedTx = new VersionedTransaction(tipMessage);
            tipVersionedTx.sign([fromKeypair]);

            txs.push(tipVersionedTx);
        } else {
            console.log('length', txs.length);
            if (txs.length > 5) throw new Error('Jito bundle can only contain 5 transactions at most.');
        }
        const bundleId = await jitoTsBackends[backendIndex].sendBundle(txs);
        if (logMessage) logMessage(`Bundle sent to backend ${backendIndex} with id: ${bundleId}`, 'success');
        return bundleId;
    }

    async function spamAndConfirmBundle(
        connection: Connection,
        txs: VersionedTransaction[],
        fromKeypair: Keypair,
        tipLamports: number,
        tipInstructionsIncluded: boolean = false,
        interval = 1000,
        timeout = 30000,
        throwError = true,
    ) {
        const backendIndex = 0;
        if (!tipInstructionsIncluded) {
            // if (txs.length === 0) throw new Error('No transactions to send');
            if (txs.length > 4) throw new Error('Jito bundle can only contain 5 transactions at most.');
            const instructions = await getTipInstructions(fromKeypair, tipLamports);

            const { blockhash } = await connection.getLatestBlockhash('finalized');
            const tipMessage = new TransactionMessage({
                payerKey: fromKeypair.publicKey,
                recentBlockhash: blockhash,
                instructions: [instructions],
            }).compileToV0Message();
            const tipVersionedTx = new VersionedTransaction(tipMessage);
            tipVersionedTx.sign([fromKeypair]);

            txs.push(tipVersionedTx);
        } else {
            console.log('length', txs.length);
            if (txs.length > 5) throw new Error('Jito bundle can only contain 5 transactions at most.');
        }
        console.log(`Sending bundle to backend ${backendIndex}...`);

        const signature = bs58.encode(txs[0].signatures[0]);

        await new Promise<void>((resolve, reject) => {
            connection.onSignature(
                signature,
                (signatureResult) => {
                    if (signatureResult.err) {
                        cleanup();
                        throwError ? reject(signatureResult.err) : console.log('error spamming');
                    } else {
                        cleanup();
                        resolve();
                    }
                },
                'processed',
            );

            const intervalId = setInterval(async () => {
                try {
                    await jitoTsBackends[backendIndex].sendBundle(txs);
                } catch (error) {
                    console.error(`Jito send bundle error`, error);
                }
            }, interval);

            const timeoutId = setTimeout(() => {
                cleanup();
                throwError ? reject(new JitoSpamAndConfirmBundleTimeoutError()) : console.log('timeout spamming');
            }, timeout);

            const cleanup = () => {
                clearInterval(intervalId);
                clearTimeout(timeoutId);
            };
        });
    }

    function isValidInstructionArray(instructions: TransactionInstruction[], signers: Keypair[], blockhash: string) {
        const message = new TransactionMessage({
            payerKey: signers[0].publicKey,
            recentBlockhash: blockhash,
            instructions: instructions,
        }).compileToV0Message();
        const versionedTx = new VersionedTransaction(message);
        versionedTx.sign(signers);

        return versionedTx.serialize().length <= 1232;
    }

    async function executeInstructions(
        connection: Connection,
        instructions: TransactionInstruction[],
        signers: Keypair[],
        feePayer: Keypair,
        jitoTip: number,
        confirm: boolean = true,
        logMessage?: (message: string, type?: 'success' | 'error') => void,
    ) {
        if (instructions.length !== signers.length) throw new Error('Instructions and signers length mismatch');

        console.log('Executing instructions...');
        console.log('Instructions length:', instructions.length);
        console.log('Signers length:', signers.length);

        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');

        const versionedTxs: VersionedTransaction[] = [];

        const currentInstructions: TransactionInstruction[] = [];
        const currentSigners: Keypair[] = [feePayer];

        for (let i = 0; i <= instructions.length; i++) {
            if (i !== instructions.length && isValidInstructionArray([...currentInstructions, instructions[i]], [...currentSigners, signers[i]], blockhash)) {
                currentInstructions.push(instructions[i]);
                currentSigners.push(signers[i]);
            } else {
                if (currentInstructions.length !== 0) {
                    const tx = new VersionedTransaction(
                        new TransactionMessage({
                            payerKey: feePayer.publicKey,
                            recentBlockhash: blockhash,
                            instructions: currentInstructions,
                        }).compileToV0Message(),
                    );
                    tx.sign(currentSigners);
                    versionedTxs.push(tx);

                    currentInstructions.length = 0;
                    currentSigners.length = 0;
                    currentSigners.push(feePayer);
                }
                if (i !== instructions.length) {
                    i--;
                }
            }
        }

        const txsToConfirm: string[] = [];
        const transactionsPerBundle = 4;
        for (let i = 0; i < versionedTxs.length; i += transactionsPerBundle) {
            const txs = versionedTxs.slice(i, i + transactionsPerBundle);
            console.log('tx length', txs[0].serialize().length);
            console.log('tip payer', signers[i].publicKey.toBase58());
            const bundleId = await spamAndConfirmBundle(connection, txs, signers[i], jitoTip);
            const sig = bs58.encode(txs[0].signatures[0]);
            txsToConfirm.push(sig);

            // if (confirm) {
            //     await connection.confirmTransaction({ blockhash: blockhash, lastValidBlockHeight: lastValidBlockHeight, signature: sig }, 'confirmed');
            // }
            if (logMessage) logMessage(`Bundle sent with id: ${bundleId}, top transaction signature ${bs58.encode(versionedTxs[0].signatures[0])}`);
        }

        return txsToConfirm;
    }

    return { sendBundle, spamAndConfirmBundle, getTipInstructions, executeInstructions };
};
